import { Component, inject, OnInit, OnDestroy, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CaseService } from '../../services/case-service/case.service';
import { LookUpService } from '../../services/lookup-service/lookup.service';
import { ActionService } from '../../services/action-service/action.service';
import { PersonModal } from '../person-modal/person-modal';
import { Person } from '../../services/case-service/case.service';
import { SearchableSelect } from '../../shared-components/searchable-select/searchable-select';
import { DatePicker } from '../../shared-components/date-picker/date-picker';
import { CharCounterComponent } from '../../shared-components/char-counter/char-counter';
import { WarningTooltip } from '../../shared-components/warning-tooltip/warning-tooltip';
import { ConfigService } from '../../core/config.service';

@Component({
  selector: 'app-edit-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, PersonModal, SearchableSelect, DatePicker, CharCounterComponent, WarningTooltip],
  templateUrl: `./edit-fields.html`,
  styleUrls: [`./edit-fields.scss`],
})
export class EditFieldsComponent implements OnInit, OnDestroy {
  private readonly caseService = inject(CaseService);
  protected readonly tooltipTexts = inject(ConfigService).getTooltipTexts();
  protected readonly actionService = inject(ActionService);
  protected readonly selectedCase = this.caseService.selectedCase;
  lookUpService = inject(LookUpService);
  protected readonly casePersons = this.caseService.casePersons;

  private readonly destroyRef = inject(DestroyRef);

  showPersonModal = false;

  private lastFocusedBeforeModal: HTMLElement | null = null;

  private readonly actionSearch$ = new Subject<string>();
  private readonly inspectorSearch$ = new Subject<string>();

  constructor() {
    effect(() => {
      if (this.selectedCase()) {
        this.actionService.setActionDate(new Date());
      }
    });
  }

  ngOnInit(): void {
    this.actionSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.lookUpService.searchActions(term));

    this.actionSearch$.next('');

    this.inspectorSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => this.lookUpService.searchInspectors(term));

    this.inspectorSearch$.next('');
  }

  ngOnDestroy(): void {
    this.actionSearch$.complete();
    this.inspectorSearch$.complete();
  }

  closePanel() {
    this.caseService.selectedCase.set(null);
  }

  goBack() {
    this.closePanel();
  }

  onActionChange(event: any): void {
    this.actionService.setSelectedPeople([]);
    const actionCode = event;

    if (!actionCode) {
      this.actionService.setSelectedAction(null);
      return;
    }

    this.actionService.setSelectedAction(actionCode);
    this.openPersonModal();
  }

  onActionSearchChange(term: string): void {
    this.actionSearch$.next(term);
  }

  onInspectorSearch(term: string): void {
    this.inspectorSearch$.next(term);
  }

  onActionScrollToEnd(): void {
    this.lookUpService.loadMoreActions();
  }

  onInspectorScrollToEnd(): void {
    this.lookUpService.loadMoreInspectors();
  }

  openPersonModal(): void {
    // Remember what had focus right before the modal opens, so we have a
    // sensible fallback to restore to if the modal is closed without a
    // successful selection.
    this.lastFocusedBeforeModal = document.activeElement as HTMLElement;
    this.showPersonModal = true;
  }

  onPersonModalClosed(result: any): void {
    this.showPersonModal = false;

    if (!result) {
      this.restoreFocusAfterModal();
      return;
    }

    const selectedPeople: Person[] = result.selectedPeople ?? [];

    if (selectedPeople.length === 0) {
      this.caseService.showToast('Kindly select a person to proceed', 'error');
      this.showPersonModal = true; // reopened — don't restore focus yet
      return;
    }

    this.actionService.setSelectedPeople(selectedPeople);
    this.caseService.showToast(`${selectedPeople.length} person(s) selected.`, 'success');
    this.restoreFocusAfterModal();
  }

  canCreateLetter(): boolean {
    const hasAction = !!this.actionService.selectedAction();
    const hasPerson = this.actionService.selectedPeopleForAction().length > 0;
    const hasDate = !!this.actionService.actionDate();

    return hasAction && hasPerson && hasDate;
  }

  createLetter(): void {
    const caseId = this.selectedCase()?.caseNo || '';
    this.actionService.createLetter(caseId);

    this.actionSearch$.next('');
    this.inspectorSearch$.next('');
  }

  reset() {
    this.actionService.reset();
    this.actionService.setActionDate(new Date());

    this.actionSearch$.next('');
    this.inspectorSearch$.next('');
  }

  private restoreFocusAfterModal(): void {
    setTimeout(() => {
      const routeToEl = document.getElementById('route-to-select');
      if (routeToEl) {
        routeToEl.focus();
      } else if (this.lastFocusedBeforeModal && document.body.contains(this.lastFocusedBeforeModal)) {
        this.lastFocusedBeforeModal.focus();
      }
      this.lastFocusedBeforeModal = null;
    });
  }
}